import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, createVNode as _createVNode, createElementBlock as _createElementBlock, normalizeClass as _normalizeClass, withCtx as _withCtx, Fragment as _Fragment } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "row items-center justify-between uploader-element"
}
const _hoisted_2 = { class: "text-overline" }
const _hoisted_3 = {
  key: 1,
  class: "row items-center"
}

import { Platform, QFile } from "quasar"
import SfxBtnIcon from "src/components/button/SfxBtnIcon.vue"
import SfxBtnOutline from "src/components/button/SfxBtnOutline.vue"
import SfxImageGallerySmall from "src/components/images/SfxImageGallerySmall.vue"
import { Log } from "src/util/classes/log"
import { IMAGE_FILE_MAIN_MIME_TYPES } from "src/util/constants/files"
import { Icons } from "src/util/enums/fontAwesomeIcons"
import { uploadImagesInApp } from "src/util/functions/appImages"
import { undoNotificationDialog } from "src/util/functions/dialogs"
import { ImageGalleryItem } from "src/util/types/components/sfxImageGallery"
import { AttachmentUpload } from "src/util/types/dataModels/attachment"
import { computed, PropType, ref, watch, watchEffect } from "vue"
import { useI18n } from "vue-i18n"

//region Props and Emits


export default /*#__PURE__*/_defineComponent({
  __name: 'SfxImageUploader',
  props: {
  uploadedImages: {
    type: Array as PropType<AttachmentUpload[]>,
  },

  required: {
    type: Boolean,
    default: false,
  },

  disable: {
    type: Boolean,
    default: false,
  },
},
  emits: ["update:uploadedImages"],
  setup(__props, { emit: __emit }) {

const props = __props

const emit = __emit
//endregion

//region Variables

const { t } = useI18n()
const title = t("sfxImage.title_photographs")

const inputFileField = ref<QFile | undefined>()

const imageFiles = ref()
let deletedImage: AttachmentUpload | undefined

const images = computed(() => props.uploadedImages || [])

const hasImages = computed(() => images.value.length > 0)

const imageGalleryItems = computed(() => getImageFromAttachment(images.value))

const capacitorEnv = computed(() => Platform.is.capacitor)
const classes = computed(() => {
  return {
    "sfx-image-uploader-required": props.required,
    "sfx-image-uploader-disable": props.disable,
  }
})

//endregion

//region Functions

async function getImage() {
  if (capacitorEnv.value) {
    try {
      const imageData = await uploadImagesInApp()

      if (imageData) {
        const imageAttachment: AttachmentUpload = { name: imageData.name, data: imageData.data }
        images.value?.push(imageAttachment)
      }
    } catch (error) {
      Log.error({ message: "Failed to fetch images in android", error })
    }
  } else {
    //Activate the hidden QFile component to fetch the files
    inputFileField.value?.pickFiles()
  }
}

function getImageFromAttachment(images: AttachmentUpload[]): ImageGalleryItem[] {
  let imageGalleryItems: ImageGalleryItem[] = []

  for (const image of images) {
    const imageData: ImageGalleryItem = {
      attachmentData: {
        name: image.name || "", // Name would never be "" in this scenario
        extension: image.name?.split(".").pop() || "",
      },
      src: URL.createObjectURL(image.data),
    }
    imageGalleryItems.push(imageData)
  }
  return imageGalleryItems
}

function handleDelete() {
  //Deleting the last image
  deletedImage = images.value?.pop()

  undoNotificationDialog(t("notifications.photo_removed"), undo)
}

// Undo button functionality
async function undo() {
  if (deletedImage) {
    images.value?.push(deletedImage)
    deletedImage = undefined
  }
}

//endregion

//region Setup Code

//Watching for any images coming from QFile component
watch(
  () => imageFiles.value,
  () => {
    let imageData: AttachmentUpload[] = []
    for (const imageFile of imageFiles.value) {
      const image = {
        name: imageFile.name,
        data: imageFile,
      }
      imageData.push(image)
    }
    images.value?.push(...imageData)
  }
)

watchEffect(() => emit("update:uploadedImages", images.value))

//endregion

return (_ctx: any,_cache: any) => {
  const _component_q_icon = _resolveComponent("q-icon")!
  const _component_q_card = _resolveComponent("q-card")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    (!capacitorEnv.value)
      ? (_openBlock(), _createBlock(_unref(QFile), {
          key: 0,
          class: "hidden",
          ref_key: "inputFileField",
          ref: inputFileField,
          modelValue: imageFiles.value,
          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((imageFiles).value = $event)),
          accept: _unref(IMAGE_FILE_MAIN_MIME_TYPES),
          multiple: ""
        }, null, 8, ["modelValue", "accept"]))
      : _createCommentVNode("", true),
    _createVNode(_component_q_card, {
      flat: "",
      class: _normalizeClass([classes.value, "q-pa-md sfx-image-uploader"])
    }, {
      default: _withCtx(() => [
        (props.uploadedImages?.length === 0)
          ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
              _createElementVNode("div", _hoisted_2, _toDisplayString(_unref(title)), 1),
              _createVNode(_component_q_icon, {
                name: _unref(Icons).SQUARE_PLUS,
                size: "sm",
                onClick: getImage
              }, null, 8, ["name"])
            ]))
          : (_openBlock(), _createElementBlock("div", _hoisted_3, [
              _createVNode(SfxImageGallerySmall, {
                class: "col",
                "image-src": imageGalleryItems.value
              }, null, 8, ["image-src"]),
              _createVNode(SfxBtnIcon, {
                onClick: _cache[1] || (_cache[1] = ($event: any) => (handleDelete())),
                icon: _unref(Icons).DELETE,
                size: "18px",
                disable: !hasImages.value,
                class: "q-mx-sm"
              }, null, 8, ["icon", "disable"]),
              _createVNode(SfxBtnOutline, {
                onClick: _cache[2] || (_cache[2] = ($event: any) => (getImage())),
                icon: _unref(Icons).ADD_PHOTO
              }, null, 8, ["icon"])
            ]))
      ]),
      _: 1
    }, 8, ["class"])
  ], 64))
}
}

})